import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  BellRing, 
  HelpCircle, 
  ShieldCheck, 
  FileText, 
  Save,
  Globe,
  Bug,
} from 'lucide-react'
import { CrudWorkspace } from '@/components/shared/crud-workspace'
import { SectionCard } from '@/components/shared/section-card'
import { useFirestoreCrud } from '@/hooks'
import { SupportTicket, FaqArticle, UserManual, BugReport } from '@/types'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { getDocument, upsertDocument } from '@/lib/firestore-crud'

type TabType = 'tickets' | 'bugs' | 'faq' | 'legal' | 'manuals'
type LangTab = 'id' | 'en'

export function SupportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') || 'tickets') as TabType
  
  const tickets = useFirestoreCrud<SupportTicket>('supportTickets')
  const faq = useFirestoreCrud<FaqArticle>('faqArticles')
  const manuals = useFirestoreCrud<UserManual>('userManuals')
  const bugs = useFirestoreCrud<BugReport>('bugReports')

  const setActiveTab = (id: TabType) => {
    setSearchParams({ tab: id })
  }

  // Legal contents state — bilingual
  const [legal, setLegal] = useState({
    privacyPolicyEn: '',
    privacyPolicyId: '',
    termsOfServiceEn: '',
    termsOfServiceId: '',
  })
  const [loadingLegal, setLoadingLegal] = useState(false)
  const [legalLang, setLegalLang] = useState<LangTab>('id')

  useEffect(() => {
    if (activeTab === 'legal') {
      loadLegal()
    }
  }, [activeTab])

  async function loadLegal() {
    setLoadingLegal(true)
    try {
      const privacy = await getDocument<any>('systemConfig', 'privacyPolicy')
      const tos = await getDocument<any>('systemConfig', 'termsOfService')
      setLegal({
        privacyPolicyEn: privacy?.contentEn || privacy?.content || '',
        privacyPolicyId: privacy?.contentId || '',
        termsOfServiceEn: tos?.contentEn || tos?.content || '',
        termsOfServiceId: tos?.contentId || '',
      })
    } catch (e) {
      console.error('Failed to load legal docs', e)
    } finally {
      setLoadingLegal(false)
    }
  }

  async function saveLegal(type: 'privacyPolicy' | 'termsOfService') {
    const isPrivacy = type === 'privacyPolicy'
    try {
      await upsertDocument('systemConfig', type, {
        contentEn: isPrivacy ? legal.privacyPolicyEn : legal.termsOfServiceEn,
        contentId: isPrivacy ? legal.privacyPolicyId : legal.termsOfServiceId,
        type,
        updatedAt: new Date().toISOString()
      })
      alert('Dokumen berhasil disimpan!')
    } catch (e) {
      alert('Gagal menyimpan dokumen.')
    }
  }

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: 'tickets', label: 'Support Tickets', icon: BellRing },
    { id: 'bugs', label: 'Bug Reports', icon: Bug },
    { id: 'faq', label: 'FAQ Articles', icon: HelpCircle },
    { id: 'legal', label: 'Legal & Policy', icon: ShieldCheck },
    { id: 'manuals', label: 'User Manuals', icon: FileText },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 border-b border-stone-800 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition ${
              activeTab === tab.id
                ? 'border-b-2 border-orange-500 text-orange-500'
                : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'tickets' && (
        <CrudWorkspace<SupportTicket>
          title="Support Tickets"
          description="Kelola tiket bantuan dari user aplikasi mobile."
          fields={[
            { key: 'subject', label: 'Subjek', type: 'text', placeholder: 'Judul masalah...' },
            { key: 'uid', label: 'User ID', type: 'text', readOnly: true },
            { key: 'email', label: 'Contact Email', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: ['open', 'in_progress', 'resolved', 'closed'] },
            { key: 'message', label: 'Pesan', type: 'textarea' },
          ]}
          items={tickets.items}
          onSave={(data) => {
            void tickets.upsert(data)
          }}
          onDelete={tickets.remove}
          emptyTitle="Belum ada tiket support"
          emptyDescription="User belum mengirimkan laporan atau pertanyaan bantuan."
        />
      )}

      {activeTab === 'bugs' && (
        <CrudWorkspace<BugReport>
          title="Bug Reports"
          description="Laporan masalah teknis yang dideteksi atau dilaporkan oleh user."
          fields={[
            { key: 'uid', label: 'Reported By (UID)', type: 'text', readOnly: true },
            { key: 'status', label: 'Status', type: 'select', options: ['new', 'assigned', 'fixed', 'closed'] },
            { key: 'details', label: 'Detail Masalah', type: 'textarea' },
            { key: 'deviceInfo', label: 'Informasi Perangkat', type: 'json', readOnly: true },
          ]}
          items={bugs.items}
          onSave={(data) => {
            void bugs.upsert(data)
          }}
          onDelete={bugs.remove}
          emptyTitle="Tidak ada laporan bug"
          emptyDescription="Bagus! Sepertinya sistem berjalan lancar."
        />
      )}

      {activeTab === 'faq' && (
        <CrudWorkspace<FaqArticle>
          title="FAQ Articles"
          description="Kelola FAQ bilingual (ID + EN). Field 'question/answer' untuk tampilan default, 'questionId/answerId' untuk Bahasa Indonesia di mobile."
          fields={[
            { key: 'question', label: '❓ Pertanyaan (EN)', type: 'text', placeholder: 'How do I pair my watch?' },
            { key: 'questionId', label: '❓ Pertanyaan (ID)', type: 'text', placeholder: 'Bagaimana cara pairing jam tangan?' },
            { key: 'category', label: 'Kategori', type: 'select', options: ['General', 'Sync', 'Hardware', 'Profile'] },
            { key: 'order', label: 'Urutan Tampil', type: 'number', placeholder: '1, 2, 3...' },
            { key: 'status', label: 'Status', type: 'select', options: ['active', 'inactive'] },
            { key: 'answer', label: '💬 Jawaban (EN)', type: 'richtext' },
            { key: 'answerId', label: '💬 Jawaban (ID)', type: 'richtext' },
          ]}
          items={faq.items}
          onSave={(data) => {
            void faq.upsert(data)
          }}
          onDelete={faq.remove}
          emptyTitle="FAQ masih kosong"
          emptyDescription="Tambahkan artikel FAQ pertama kamu untuk membantu user."
        />
      )}

      {activeTab === 'legal' && (
        <div className="grid gap-6">
          {/* Language Tab Switcher */}
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-stone-500" />
            <span className="text-sm text-stone-500 mr-2">Bahasa konten:</span>
            {(['id', 'en'] as LangTab[]).map((lang) => (
              <button
                key={lang}
                onClick={() => setLegalLang(lang)}
                className={`px-4 py-1.5 rounded-xl text-sm font-bold transition ${
                  legalLang === lang
                    ? 'bg-orange-500 text-white'
                    : 'bg-stone-800 text-stone-400 hover:text-stone-200'
                }`}
              >
                {lang === 'id' ? '🇮🇩 Bahasa Indonesia' : '🇺🇸 English'}
              </button>
            ))}
          </div>

          {loadingLegal ? (
            <div className="py-10 text-center text-stone-500 italic">Memuat dokumen legal...</div>
          ) : (
            <>
              <SectionCard 
                title={`Privacy Policy — ${legalLang === 'id' ? 'Bahasa Indonesia' : 'English'}`}
                description="Kebijakan privasi — sumber tunggal dari Firestore, digunakan di dashboard dan aplikasi mobile."
                action={
                  <button 
                    onClick={() => saveLegal('privacyPolicy')}
                    className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600 transition"
                  >
                    <Save className="h-4 w-4" /> Simpan Privacy Policy
                  </button>
                }
              >
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <FileText className="h-3 w-3" />
                    <span>Disimpan ke: <code className="text-orange-400">systemConfig/privacyPolicy.content{legalLang === 'id' ? 'Id' : 'En'}</code></span>
                  </div>
                  <RichTextEditor 
                    key={`pp-${legalLang}`}
                    value={legalLang === 'id' ? legal.privacyPolicyId : legal.privacyPolicyEn}
                    onChange={(val) => setLegal(prev => ({
                      ...prev,
                      [legalLang === 'id' ? 'privacyPolicyId' : 'privacyPolicyEn']: val
                    }))}
                    placeholder={legalLang === 'id' ? 'Tulis kebijakan privasi dalam Bahasa Indonesia...' : 'Write privacy policy in English...'}
                  />
                </div>
              </SectionCard>

              <SectionCard 
                title={`Terms of Service — ${legalLang === 'id' ? 'Bahasa Indonesia' : 'English'}`}
                description="Syarat dan ketentuan — sumber tunggal dari Firestore, digunakan di dashboard dan aplikasi mobile."
                action={
                  <button 
                    onClick={() => saveLegal('termsOfService')}
                    className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600 transition"
                  >
                    <Save className="h-4 w-4" /> Simpan Terms of Service
                  </button>
                }
              >
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <FileText className="h-3 w-3" />
                    <span>Disimpan ke: <code className="text-orange-400">systemConfig/termsOfService.content{legalLang === 'id' ? 'Id' : 'En'}</code></span>
                  </div>
                  <RichTextEditor 
                    key={`tos-${legalLang}`}
                    value={legalLang === 'id' ? legal.termsOfServiceId : legal.termsOfServiceEn}
                    onChange={(val) => setLegal(prev => ({
                      ...prev,
                      [legalLang === 'id' ? 'termsOfServiceId' : 'termsOfServiceEn']: val
                    }))}
                    placeholder={legalLang === 'id' ? 'Tulis syarat dan ketentuan dalam Bahasa Indonesia...' : 'Write terms of service in English...'}
                  />
                </div>
              </SectionCard>
            </>
          )}
        </div>
      )}

      {activeTab === 'manuals' && (
        <CrudWorkspace<UserManual>
          title="User Manuals"
          description="Kelola panduan pengguna bilingual (ID + EN). Konten digunakan oleh mobile app berdasarkan locale pengguna."
          fields={[
            { key: 'title', label: '📖 Judul Manual (ID)', type: 'text', placeholder: 'Panduan Pengaturan Awal' },
            { key: 'titleEn', label: '📖 Judul Manual (EN)', type: 'text', placeholder: 'Getting Started Guide' },
            { key: 'subtitle', label: '📝 Deskripsi (ID)', type: 'textarea' },
            { key: 'subtitleEn', label: '📝 Deskripsi (EN)', type: 'textarea' },
            { 
              key: 'type', 
              label: 'Kategori', 
              type: 'select', 
              options: ['setup', 'health', 'notifications', 'customization'] 
            },
            { key: 'order', label: 'Urutan', type: 'number', placeholder: '1, 2, 3...' },
            { key: 'status', label: 'Status', type: 'select', options: ['active', 'inactive'] },
            { key: 'content', label: '📄 Konten HTML (ID)', type: 'richtext' },
            { key: 'contentEn', label: '📄 Konten HTML (EN)', type: 'richtext' },
            { key: 'pdfUrl', label: 'File PDF (opsional)', type: 'file' },
          ]}
          items={manuals.items}
          onSave={(data) => {
            void manuals.upsert(data)
          }}
          onDelete={manuals.remove}
          emptyTitle="Belum ada manual"
          emptyDescription="Tambahkan panduan pengguna pertama. Konten HTML tersedia dalam ID dan EN."
        />
      )}
    </div>
  )
}
